import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "@mui/material/Button";
import { TextField, Link, Typography, Box, Divider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

import axios from "axios";
import useAxiosInterceptor from "../layout/AxiosInstance";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { enqueueSnackbar } from "notistack";
import { setisOpen } from "../../store/ModalSlice";
import { addBulkAttendanceExcel } from "../../Service/Apis";
import { setInPageLoading } from "../../store/LoadingSlice";
import { ToastVariant, useCustomToast } from "../Methods/SnackBar";
import WeekSelector from "../DatePickers/WeekSelector";

const headers = [
	"employee_code",
	"month_year",
	"present_days",
	"paid_leaves",
	"sick_leaves",
	"leave_without_pay",
	"regularized_days",
];

const AttendanceExcelUpload = () => {
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [error, setError] = useState<boolean>(true);
	const [useWeekSelector, setUseWeekSelector] = useState<boolean>(true);
	const [dateRange, setDateRange] = useState<{
		startDate: Dayjs | null;
		endDate: Dayjs | null;
	}>({
		startDate: null,
		endDate: null,
	});

	const dispatch = useDispatch();
	const ispageloading = useSelector(
		(state: any) => state.loading.inPageLoading
	);

	const showToast = useCustomToast();
	const token = localStorage.getItem("token");
	const axiosInstance = useAxiosInterceptor();

	// Initialize with default week (last week)
	useEffect(() => {
		if (useWeekSelector && !dateRange.startDate) {
			const today = dayjs();
			const lastWeekMonday = today
				.subtract(7, "day")
				.startOf("week")
				.add(1, "day");
			const lastWeekFriday = lastWeekMonday.add(4, "day");

			setDateRange({
				startDate: lastWeekMonday,
				endDate: lastWeekFriday,
			});
		}
	}, [useWeekSelector]);

	const handleShowToast = (
		message: string,
		variant: ToastVariant,
		isCloseable: boolean
	) => {
		showToast(message, variant, isCloseable);
	};

	const handleWeekChange = (value: {
		startDate: Dayjs | null;
		endDate: Dayjs | null;
	}) => {
		setDateRange(value);
	};

	const handleFileChange = (event: any) => {
		const file = event.target.files[0];
		const allowedExtensions = /(\.xlsx)$/i;
		if (file && !allowedExtensions.test(file.name)) {
			handleShowToast(
				"Invalid file format. Only XLSX files are allowed.",
				"error",
				true
			);
			setSelectedFile(null);
			setError(true);
		} else {
			if (
				file &&
				(file.type === "application/vnd.ms-excel" ||
					file.type ===
						"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
			) {
				setSelectedFile(file);
				setError(false);
			} else {
				setError(true);
			}
		}
	};

	const handleDownload = (e: any) => {
		const worksheet = XLSX.utils.aoa_to_sheet([headers]);
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

		const excelBuffer = XLSX.write(workbook, {
			type: "array",
			bookType: "xlsx",
		});

		const data = new Blob([excelBuffer], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		});
		saveAs(data, "attendance_bulk_upload.xlsx");
	};

	const handleUpload = () => {
		if (!dateRange.startDate || !dateRange.endDate) {
			handleShowToast("Please select a date range", "error", true);
			return;
		}

		if (!selectedFile) {
			handleShowToast("Please select a file to upload", "error", true);
			return;
		}

		dispatch(setInPageLoading(true));

		if (
			selectedFile.type !== "application/vnd.ms-excel" &&
			selectedFile.type !==
				"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
		) {
			handleShowToast(
				"Please select a valid Excel file (.xls or .xlsx)",
				"error",
				true
			);
			setError(true);
			dispatch(setInPageLoading(false));
			return;
		}

		const reader = new FileReader();
		reader.onload = (event) => {
			const data = new Uint8Array(event.target!.result as ArrayBuffer);
			const workbook = XLSX.read(data, { type: "array" });
			const worksheet = workbook.Sheets[workbook.SheetNames[0]];

			// Get raw JSON data from the sheet
			const jsonData = XLSX.utils.sheet_to_json(worksheet, {
				header: 1,
			}) as any[][];

			// Filter out empty rows
			const filteredData = jsonData.filter((row) => {
				return row.some(
					(cell) => cell !== undefined && cell !== null && cell !== ""
				);
			});

			console.log("Filtered data rows:", filteredData.length);

			const formData = new FormData();
			formData.append("file", selectedFile);
			formData.append("data", JSON.stringify(filteredData));

			// Add date range
			formData.append("start_date", dateRange.startDate!.format("YYYY-MM-DD"));
			formData.append("end_date", dateRange.endDate!.format("YYYY-MM-DD"));

			// API call for uploading
			axiosInstance
				.post(addBulkAttendanceExcel, formData, {
					headers: {
						"Content-Type": "multipart/form-data",
						Authorization: `Bearer ${token}`,
					},
				})
				.then((res) => {
					handleShowToast(
						`${res.data.message || "Data Updated Successfully!"}`,
						"success",
						true
					);
					dispatch(setInPageLoading(false));
					dispatch(setisOpen(true));
				})
				.catch((error) => {
					console.log(error);
					handleShowToast(
						`${error.response?.data?.message || "Error Occurred!"}`,
						"error",
						true
					);
					dispatch(setInPageLoading(false));
				});
		};

		reader.readAsArrayBuffer(selectedFile);
	};

	return (
		<Box sx={{ m: 1, p: 1, mb: 8 }}>
			<Typography variant="h6" gutterBottom>
				Bulk Upload Attendance
			</Typography>
			<Link
				onClick={handleDownload}
				sx={{ "&:hover": { cursor: "pointer" }, m: 1, display: "block", mb: 2 }}
			>
				Download Upload Format
			</Link>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
				<Typography variant="subtitle2" sx={{ ml: 1 }}>
					Select Week Range
				</Typography>

				<Box sx={{ ml: 1, mb: 4 }}>
					<WeekSelector
						value={dateRange}
						onChange={handleWeekChange}
						disableFuture={true}
					/>
					<Typography
						variant="caption"
						color="textSecondary"
						sx={{ mt: 1, display: "block" }}
					>
						{dateRange.startDate && dateRange.endDate ? (
							<>
								Selected: {dateRange.startDate.format("MMM DD, YYYY")} to{" "}
								{dateRange.endDate.format("MMM DD, YYYY")}
								(Week {dateRange.startDate.week()})
							</>
						) : (
							"Please select a week"
						)}
					</Typography>
				</Box>
				<TextField
					sx={{ m: 1 }}
					size="small"
					type="file"
					inputProps={{
						accept: ".xls,.xlsx",
					}}
					onChange={handleFileChange}
				/>

				<Divider sx={{ my: 1 }} />

				<Button
					variant="contained"
					onClick={handleUpload}
					disabled={
						error || ispageloading || !dateRange.startDate || !dateRange.endDate
					}
					size="small"
					sx={{ m: 2, alignSelf: "flex-start" }}
				>
					Upload
				</Button>
			</Box>
		</Box>
	);
};

export default AttendanceExcelUpload;
